/** Session state + catalog access for the exec Custom HW Pricing view.
 *  Overrides never leave this hook except as the derived customPrices map —
 *  they are NOT part of config and are lost on refresh (part 2 by design). */
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { fetchCatalogRows } from '../utils/catalogFetch';
import { snapshotFromRows } from '../pricing/rowsToSnapshot';
import { applyOverrides } from '../pricing/applyOverrides';
import {
  emptyOverrides, hasOverrides, countChanges, dollarsToMicros,
  withComponentCost, withIntegrationBp, clearSku,
} from '../domain/PricingOverrides';

export function useExecPricing({ enabled, vendor, quarter, asOf, onOverridesReset }) {
  const [status, setStatus] = useState('idle');
  const [snapshot, setSnapshot] = useState(null);
  const [overrides, setOverrides] = useState(emptyOverrides);
  const [overrideSetId, setOverrideSetId] = useState(null);
  const fetchedFor = useRef(null);
  // Generation token invalidating in-flight load()s. The quote-load flow flips
  // `enabled` before the exec record arrives, so a LIVE catalog fetch can be
  // in flight when hydrate() installs the frozen basis under the SAME
  // vendor|quarter key — the key-only staleness guard would let that late
  // response overwrite the basis (or flip a hydrated session to 'error').
  // hydrate() bumps the generation; load() commits only if it is unchanged.
  const loadGen = useRef(0);
  const prevKey = useRef(`${vendor}|${quarter}`);
  // dispatchedAsOfRef: the `asOf` value baked into the most recent load()
  // dispatch for the CURRENT fetchedFor key. Distinct from the `asOf` prop so
  // the fetch-trigger effect below can tell "we already dispatched this key
  // with today's asOf" apart from "we dispatched it before/behind the live
  // edition" (see the effect for why that earns a corrective refetch).
  //
  // NOT replaceable by the committed snapshot's own `resolvedAsOf` (SP2 Task 5
  // proposed exactly that; ruled closed won't-fix). This ref records DISPATCH
  // time — it is written twice in the trigger effect below, immediately before
  // each `load()`, and re-aligned by rebase() (which writes the accepted
  // snapshot's resolution time, not a dispatch time). The snapshot records
  // RESOLUTION time: load() commits it AFTER its await. A
  // guard derived from the snapshot therefore answers a different question
  // than this effect asks, and the two disagree for the length of one in-flight
  // fetch. What that costs, strongest first:
  //
  //  - Spurious drift inside that window, with nothing to clear it.
  //    `overrides` is already a dep of the trigger effect, so an exec edit
  //    landing while a corrective load is in flight re-runs it. A
  //    snapshot-derived guard is still false there (the OLD snapshot is the
  //    committed one), so control falls through to the hasOverrides branch and
  //    flips editionDrifted TRUE — a "Reprice" affordance for a correction
  //    already on the wire. Nor does it self-heal: once the fetch commits, the
  //    guard passes and the effect returns before ever reaching the
  //    setEditionDrifted(false) on the dispatch path, so the flag stays stuck
  //    until something else clears it — a vendor/quarter switch, hydrate(),
  //    rebase(), or a further asOf move that arrives with overrides already
  //    cleared (which reaches the dispatch path below). None of those is
  //    implied by the drift itself: the last route needs a SECOND republish
  //    on top of cleared overrides, so the spurious flag can persist
  //    arbitrarily long in the meantime. Today the
  //    dispatched-asOf check returns early and no flag is raised. Needs no
  //    snapshot dep and no mock — this one is reachable as the code stands.
  //  - Drifted before the first load. The proposed derivation has no null
  //    guard: with `snapshot` still null at mount and an edition published,
  //    `asOf !== undefined` is true, so the session reads as drifted with no
  //    basis to reprice.
  //  - The hydrate() boundary. A basisSnapshot persisted before resolvedAsOf
  //    existed carries none, so the derivation reads `defined !== undefined`
  //    -> drifted, contradicting hydrate()'s explicit invariant below that a
  //    frozen basis is never drifted.
  //  - Intent vs. result. This ref asserts a fact this hook CONTROLS ("we
  //    asked for this asOf"). A derived guard asserts a property of data that
  //    arrived from elsewhere, so hydrate(), rebase(), or any future writer of
  //    the snapshot cell silently re-arms the fetch trigger.
  //
  // Scope note, so this is not re-argued from a test count: 7 of the 14 tests
  // in useExecPricingAsOf.test.js do fail on the guard change alone, but they
  // jest.mock snapshotFromRows and return fixtures with no resolvedAsOf, which
  // is what leaves the derived guard permanently unsatisfied there. In
  // PRODUCTION load() commits a snapshot whose resolvedAsOf IS the asOf it was
  // dispatched with, so the guard does settle and there is NO second catalog
  // fetch. That claim was made in an earlier draft and is false.
  const dispatchedAsOfRef = useRef(undefined);
  // hydratedRef: true while the displayed snapshot came from hydrate() (a
  // frozen, saved-quote basis) rather than a live fetch. Gates the
  // asOf-correction below — a hydrated basis must never be silently replaced
  // by a live fetch; only a real vendor/quarter switch (which redispatches
  // unconditionally) or an explicit hydrate/rebase may replace it.
  const hydratedRef = useRef(false);
  // Mirrors `asOf` so rebase() (a stable-identity useCallback) can align
  // dispatchedAsOfRef with the CURRENT live asOf without depending on it.
  const asOfRef = useRef(asOf);
  asOfRef.current = asOf;
  // editionDrifted: true when the live edition's asOf has moved past what
  // this session is showing AND there are overrides in play, so the
  // correction below is deliberately withheld (see the fetch-trigger effect).
  // Surfaced to the view so the staleness is never silent even when it can't
  // be auto-corrected — see ExecPricingView's "Reprice" affordance.
  const [editionDrifted, setEditionDrifted] = useState(false);
  // Mirrors `overrides` for the vendor/quarter-switch effect below so that
  // effect can read the current value without depending on it (adding
  // `overrides` to that effect's deps would make every edit re-run the
  // switch-detection logic). Updated on every render, ahead of effects.
  const overridesRef = useRef(overrides);
  overridesRef.current = overrides;
  // Mirrors `onOverridesReset` callback for the vendor/quarter-switch effect
  // so the effect can call the callback without depending on it.
  const onOverridesResetRef = useRef(onOverridesReset);
  onOverridesResetRef.current = onOverridesReset;

  const load = useCallback(async () => {
    const key = `${vendor}|${quarter}`;  // Capture key + generation at call time
    const gen = loadGen.current;
    // Commit only if the key is still current (stale-response guard) AND no
    // hydrate() superseded this fetch meanwhile (generation guard).
    const stillCurrent = () => fetchedFor.current === key && loadGen.current === gen;
    setStatus('loading');
    try {
      const { components, compositions } = await fetchCatalogRows(vendor);
      if (stillCurrent()) {
        // The empty-resolution guard (an unseeded vendor, or an out-of-range
        // quarter, must fail loudly rather than render a believable "0.00%"
        // integration rate) lives in snapshotFromRows itself — see its doc.
        // asOf = the live edition's transaction-time cutoff. Without it the exec
        // would snapshot rows nobody has published yet, so "reset to catalog"
        // could show a price no rep can see. The empty-resolution guard lives
        // inside snapshotFromRows — do not hand-copy one here.
        const resolved = snapshotFromRows(vendor, components, compositions, quarter, asOf);
        setSnapshot(resolved);
        setStatus('ready');
        hydratedRef.current = false;   // belt-and-suspenders: any committed load is live, never hydrated
      }
    } catch (e) {
      console.error('Catalog fetch failed:', e);
      if (stillCurrent()) {
        setStatus('error');
      }
    }
  }, [vendor, quarter, asOf]);

  useEffect(() => {
    const key = `${vendor}|${quarter}`;
    if (!enabled) return;
    if (fetchedFor.current !== key) {
      fetchedFor.current = key;
      dispatchedAsOfRef.current = asOf;
      hydratedRef.current = false;     // a real vendor/quarter switch always leaves hydrated mode
      setEditionDrifted(false);
      load();
      return;
    }
    if (hydratedRef.current) return;               // frozen basis: never auto-touched
    if (dispatchedAsOfRef.current === asOf) return; // nothing pending for this key
    // The live edition's asOf has moved since our last dispatch for this key
    // — either undefined -> defined (usePriceEditions resets `edition` to
    // null on every vendor switch before its async reload resolves, so the
    // FIRST live fetch for a key can go out un-scoped/"latest catalog"
    // before the edition arrives) or a later defined -> different-defined
    // republish landing mid-session (revalidate-on-focus in
    // usePriceEditions.js calls load() WITHOUT nulling `edition` first, so
    // this is reachable while the panel stays open, not just at mount).
    //
    // Auto-pick it up ONLY while there is nothing to protect: silently
    // rebasing a session that already carries overrides would move the
    // pricing basis with no audit trail (unlike the exec-confirmed
    // reprice/rebase flow — App.js startReprice/acceptReprice — which diffs
    // the drift, requires explicit accept, AND records a 'reprice' audit
    // event). While overrides are active, leave dispatchedAsOfRef stale and
    // flip editionDrifted instead, so the staleness is surfaced (the
    // ExecPricingView "Reprice" affordance) rather than silent, and re-check
    // the moment overrides clear (overrides is a dep below) so a later
    // "reset to catalog" is never stale either.
    if (hasOverrides(overrides)) {
      setEditionDrifted(true);
      return;
    }
    dispatchedAsOfRef.current = asOf;
    setEditionDrifted(false);
    // Bump the generation so a still-in-flight (or already-committed) fetch
    // dispatched with the OLD asOf for this SAME key cannot win a race
    // against this corrective one — same pattern hydrate() uses below to
    // invalidate an in-flight fetch.
    loadGen.current += 1;
    load();
  }, [enabled, vendor, quarter, asOf, load, overrides]);

  useEffect(() => {                       // vendor/quarter switch resets overrides
    const key = `${vendor}|${quarter}`;
    if (key !== prevKey.current) {
      prevKey.current = key;
      // Side effect (callback) lives in the effect body, not inside the
      // setOverrides updater — updaters can run twice under StrictMode dev
      // double-invocation, which would invoke the callback twice. Read the
      // pre-switch overrides via the ref (kept in sync every render) so this
      // effect doesn't need `overrides` in its deps.
      // Reset ONLY when there is something to reset. With overrides already
      // empty, setOverrides(emptyOverrides()) would be a semantic no-op that
      // still mints a new overrides identity — recomputing the customPrices
      // memo and firing the App-side mirror, which clobbered a pinned
      // saved-price display during quote load (quarter propagation). Stale
      // costs still never carry: any non-empty overrides always reset here.
      if (hasOverrides(overridesRef.current)) {
        onOverridesResetRef.current?.('Custom pricing was reset — vendor or quarter changed.');
        setOverrides(emptyOverrides());
      }
    }
  }, [vendor, quarter]);

  useEffect(() => {
    // overrideSetId lifecycle: null when no overrides, eps-<id> when overrides exist.
    // Regenerates when overrides transition from empty->non-empty.
    const has = hasOverrides(overrides);
    setOverrideSetId(prev => {
      if (has && !prev) return `eps-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
      if (!has && prev) return null;
      return prev;
    });
  }, [overrides]);

  const { customPriceDollars, customSkus } = useMemo(
    () => (snapshot ? applyOverrides(snapshot, overrides)
                    : { customPriceDollars: {}, customSkus: new Set() }),
    [snapshot, overrides]);

  const setComponentCost = useCallback((productSku, componentSku, dollars) =>
    setOverrides(o => withComponentCost(o, productSku, componentSku,
      dollars === null ? null : dollarsToMicros(dollars))), []);
  const setIntegrationPct = useCallback((pct) => {
    if (pct === null) return setOverrides(o => withIntegrationBp(o, null));
    if (!(pct >= 1)) throw new RangeError(`${pct}% is not a valid % for this field`);
    setOverrides(o => withIntegrationBp(o, Math.round(pct * 100)));
  }, []);

  // Part 3: restore a persisted session from its FROZEN basis (spec §2.3).
  // Marks the loaded vendor|quarter pair as both fetched and "previous" so the
  // fetch effect doesn't refire over the basis and the switch-reset effect
  // doesn't treat the load's own vendor/quarter propagation as a user switch.
  const hydrate = useCallback(({ snapshot: basis, overrides: o, overrideSetId: id, vendor: v, quarter: q }) => {
    const key = `${v}|${q}`;
    fetchedFor.current = key;
    prevKey.current = key;
    loadGen.current += 1;   // invalidate any in-flight live fetch — it must not overwrite the basis
    hydratedRef.current = true;   // freeze: no live fetch (incl. an asOf correction) may replace this basis
    setEditionDrifted(false);     // a frozen basis is never "drifted" — nothing to auto-correct or reprice
    setSnapshot(basis);
    setOverrides(o);
    setOverrideSetId(id);
    setStatus('ready');
  }, []);

  // Part 3: reprice accept — the catalog underneath moves, the exec's word
  // (overrides) and the audit thread (overrideSetId) survive (spec §2.5).
  //
  // newSnapshot.resolvedAsOf: the asOf the ACCEPTED candidate was actually
  // built from. The candidate carries its own cutoff (CatalogSnapshot
  // .resolvedAsOf, set by snapshotFromRows inside buildRepriceCandidate), so
  // rebase reads it off the snapshot rather than taking it as a second
  // argument threaded through App.js's repriceDiff state. It must be the
  // cutoff the candidate itself was resolved against, not asOfRef.current
  // (today's live value AT ACCEPT TIME): the reprice dialog can sit open for a
  // while under review, and if the edition republishes while it's up, those
  // two values diverge. Reading asOfRef.current instead would silently report
  // the session as synced to an edition the accepted candidate never actually
  // reflects (the exact "pinned to a superseded edition" state this hook
  // exists to surface, just via the accept path).
  //
  // undefined is a legitimate value here, not a missing one: a vendor with no
  // published edition resolves its candidate with asOf undefined, and the live
  // `asOf` prop is undefined too, so the comparison below correctly reports
  // "not drifted". No hydrated (legacy, pre-resolvedAsOf) basis can reach this
  // path — rebase only ever receives a freshly built candidate.
  //
  // If resolvedAsOf differs from the CURRENT live asOf, the edition moved
  // again during review: leave editionDrifted up so it keeps inviting
  // another reprice — that is the correct outcome, not a bug. Only when
  // they match is the session actually caught up, and the flag clears.
  //
  // hydratedRef.current = false: hydratedRef exists to stop a corrective
  // refetch from stomping a hydrated saved-quote basis — the ORIGINAL
  // snapshot passed to hydrate(). After rebase(), that snapshot is gone;
  // `newSnapshot` (the freshly accepted candidate) is now current, and it is
  // tracked going forward exactly like a live-fetched one (dispatchedAsOfRef/
  // editionDrifted are already set above). There is nothing left from the
  // hydrate to protect, so the gate must come down here — every hydrate() in
  // this codebase carries overrides (App.js:907-910, loading a saved
  // custom-priced quote), so the Reprice button — and therefore rebase() —
  // is reachable immediately after every hydrate. Leaving hydratedRef true
  // would make the fetch-trigger effect's `if (hydratedRef.current) return;`
  // (the FIRST check after a same-key render) swallow every future asOf
  // change unconditionally: no corrective refetch, no editionDrifted, for
  // the rest of the session — worse than the round-3 gap, since that was
  // bounded to one dialog's open window and this would be indefinite. Only
  // a real vendor/quarter switch or another hydrate() would re-arm it.
  const rebase = useCallback((newSnapshot) => {
    const { resolvedAsOf } = newSnapshot;
    dispatchedAsOfRef.current = resolvedAsOf;
    setEditionDrifted(resolvedAsOf !== asOfRef.current);
    hydratedRef.current = false;
    setSnapshot(newSnapshot);
  }, []);

  return {
    status, snapshot, overrides, overrideSetId,
    customPrices: customPriceDollars, customSkus,
    changeCount: countChanges(overrides),
    editionDrifted,
    setComponentCost, setIntegrationPct,
    resetSku: useCallback((sku) => setOverrides(o => clearSku(o, sku)), []),
    resetAll: useCallback(() => setOverrides(emptyOverrides()), []),
    retry: load,
    hydrate, rebase,
  };
}

/** App-side mirror of execPricing.customPrices into the App's plain
 *  customPrices state, with the part-3 saved-price pin (spec §2.2).
 *
 *  Loading a custom-priced quote pins the SAVED outputs into the display
 *  immediately (pinSavedPrices). Until the session is restored, the hook's
 *  derived customPrices reflect live-catalog machinery — an enable-triggered
 *  fetch committing a snapshot, or reset churn — not prices the exec ever
 *  accepted, so mirroring them would clobber the pin (catalog prices shown
 *  while the UI claims "prices shown as saved"). The mirror therefore holds
 *  while a pin is active AND the hook owns no overrides; the pin releases the
 *  moment the hook holds real overrides again — hydrate() restoring the saved
 *  session (derived == saved, cent-exact, per the integrity check) or a fresh
 *  exec edit. releasePin() is for clean-quote loads, where the pin must yield
 *  without any overrides appearing.
 *
 *  Lives beside useExecPricing (not in App.js) so the pin/mirror interplay is
 *  provable with renderHook tests; App.js owns the state cell because it must
 *  exist before useConfigurationLogic runs (see the hook-order note there). */
export function useSavedPricePin(execPricing, setCustomPrices) {
  const pinnedRef = useRef(false);
  const { customPrices, overrides } = execPricing;
  useEffect(() => {
    if (pinnedRef.current && !hasOverrides(overrides)) return;   // pin stands
    pinnedRef.current = false;
    setCustomPrices(customPrices);
  }, [customPrices, overrides, setCustomPrices]);
  return {
    pinSavedPrices: useCallback((savedPrices) => {
      pinnedRef.current = true;
      setCustomPrices(savedPrices);
    }, [setCustomPrices]),
    releasePin: useCallback(() => { pinnedRef.current = false; }, []),
  };
}
